export interface Plan {
  name: string;
  description: string;
  price: number;
  credits: number;
  period: 'lifetime' | 'monthly';
  features: string[];
  stripePriceId?: string;
}

export const PLANS: Record<string, Plan> = {
  free: {
    name: 'Free',
    description: 'Get started for free',
    price: 0,
    credits: 0,
    period: 'lifetime',
    features: [
      'Create projects with AI',
      'Manage your content',
      'Basic support',
    ],
  },
  pro: {
    name: 'Pro',
    description: 'For creators who need more',
    price: 29,
    credits: parseInt(import.meta.env.VITE_CREDIT_LIMIT_PRO!, 10),
    period: 'monthly',
    features: [
      'Standard usage limits',
      'All AI models',
      'Priority support',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PRO_PRICE_ID,
  },
  max: {
    name: 'Max',
    description: 'For production workflows',
    price: 99,
    credits: parseInt(import.meta.env.VITE_CREDIT_LIMIT_MAX!, 10),
    period: 'monthly',
    features: [
      'High-volume usage limits',
      'All AI models',
      'Priority support',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_MAX_PRICE_ID,
  },
  ultra: {
    name: 'Ultra',
    description: 'Maximum power for teams',
    price: 249,
    credits: parseInt(import.meta.env.VITE_CREDIT_LIMIT_ULTRA!, 10),
    period: 'monthly',
    features: [
      'Highest usage limits',
      'All AI models',
      'Priority support',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_ULTRA_PRICE_ID,
  },
};

// Extract base plan from effective plan string (e.g., "pro-trialing" → "pro")
export function getBasePlan(effectivePlan: string): string {
  const base = effectivePlan.split('-')[0];
  return base in PLANS ? base : effectivePlan;
}

export function getPlanByName(name: string): Plan | undefined {
  return PLANS[name.toLowerCase()];
}

export function getCreditLimit(planName: string): number {
  const plan = PLANS[planName.toLowerCase()];
  return plan?.credits ?? 0;
}

export function canAfford(
  planName: string,
  creditsUsed: number,
  creditsNeeded: number
): boolean {
  const limit = getCreditLimit(planName);
  return creditsUsed + creditsNeeded <= limit;
}
